import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { http } from "../../common/lib";
import config from "../../config";
import { actionCreators, actionTypes } from "./actions";
import { logToSentry } from "../../config/Sentry";

/**
 * Build the Error for an abandoned-close response that reported failures.
 * The API answers 207 Multi-Status with { succeeded: [], failed: [] } when
 * some or all of the ids could not be abandoned. 207 is a 2xx, so http.js
 * resolves it like any success — without this the saga dispatched
 * bulkAbandonedCloseSuccess and the UI showed "audit abandoned" for audits
 * that were never touched.
 */
const abandonedCloseError = (failed, succeeded) => {
    const reasons = failed
        .map((item) => item && (item.error || item.reason))
        .filter(Boolean);
    const error = new Error(
        reasons.length
            ? `Abandon failed for ${failed.length} audit(s): ${reasons.join("; ")}`
            : `Abandon failed for ${failed.length} audit(s).`
    );
    error.name = "AbandonedCloseFailure";
    // Carried so a caller can tell "nothing worked" from "some worked" without
    // re-parsing the message.
    error.failed = failed;
    error.succeeded = succeeded;
    return error;
};

function* bulkAbandonedClose(action) {
    const { auditIds } = action.payload;
    const url = `${config.api.paths.auditAbandonedClose}abandoned-close/`;
    try {
        const res = yield call(http.authorized.post, url, { auditIds });
        const succeeded = res?.data?.succeeded ?? [];
        const failed = res?.data?.failed ?? [];

        if (failed.length > 0) {
            throw abandonedCloseError(failed, succeeded);
        }

        yield put(actionCreators.bulkAbandonedCloseSuccess());
    } catch (error) {
        logToSentry("On bulk audit abandoned close:", { error });
        yield put(actionCreators.bulkAbandonedCloseFailure(error));
    }
}

function* close(action) {
    const { auditId } = action.payload;
    try {
        const { data } = yield call(
            http.authorized.post,
            config.api.paths.auditClose,
            { audit: auditId }
        );
        yield put(actionCreators.closeSuccess(data));
    } catch (error) {
        logToSentry("On audit close:", { error });
        yield put(actionCreators.closeFailure(error));
    }
}

function* get(action) {
    try {
        const status = action.payload ? action.payload.status : null;
        const { id } = yield select((state) => state.userOrganisations.item);

        const url = `${
            config.api.paths.audits
        }?${`organisation=${id}&tab=${status}`}`;

        const { data } = yield call(http.authorized.get, url);
        yield put(actionCreators.getSuccess(data));
    } catch (error) {
        logToSentry("On audits get:", { error });
        yield put(actionCreators.getFailure(error));
    }
}
function* patch(action) {
    const { audit } = action.payload;
    const url = `${config.api.paths.audits}${audit.id}/`;
    try {
        const { data } = yield call(http.authorized.patch, url, audit);
        yield put(actionCreators.patchSuccess(data));
    } catch (error) {
        console.error(error);
        logToSentry("On audit patch:", { error });
        yield put(actionCreators.patchFailure(error));
    }
}
function* post(action) {
    const { audits } = action.payload;
    try {
        const { data } = yield call(
            http.authorized.post,
            config.api.paths.audits,
            audits
        );
        yield put(actionCreators.postSuccess(data));
    } catch (error) {
        console.error(error);
        logToSentry("On audits post:", { error });
        yield put(actionCreators.postFailure(error));
    }
}

export default function* () {
    yield all([
        takeEvery(actionTypes.AUDIT_BULK_ABANDONED_CLOSE_REQUEST, bulkAbandonedClose),
        takeLatest(actionTypes.AUDIT_CLOSE_REQUEST, close),
        takeLatest(actionTypes.AUDITS_GET_REQUEST, get),
        takeLatest(actionTypes.AUDIT_PATCH_REQUEST, patch),
        takeLatest(actionTypes.AUDITS_POST_REQUEST, post)
    ]);
}
